class ProductCardVideo extends HTMLElement {
  #observer;
  #abortController;
  #motionPreference;
  #video;
  #visible = false;
  #pending = false;
  #syncRequested = false;
  #failed = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionPreference.addEventListener('change', () => this.#syncPlayback(), { signal });
    document.addEventListener('visibilitychange', () => this.#syncPlayback(), { signal });
    this.addEventListener('playing', () => {
      if (this.#shouldPlay()) this.setAttribute('playing', '');
      else this.#stop();
    }, { capture: true, signal });
    this.addEventListener('error', (event) => {
      if (event.target === this.#video) {
        this.#failed = true;
        this.#stop();
      }
    }, { capture: true, signal });
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting && entry.intersectionRatio > 0.5;
      this.#syncPlayback();
    }, { threshold: [0, 0.5, 1] });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#visible = false;
    this.#observer?.disconnect();
    this.#abortController?.abort();
    this.#stop();
  }

  #shouldPlay() {
    return this.isConnected && this.#visible && !document.hidden &&
      !this.#motionPreference.matches && !this.#failed;
  }

  #stop() {
    this.#video?.pause();
    this.removeAttribute('playing');
  }

  async #syncPlayback() {
    if (!this.#shouldPlay()) {
      this.#stop();
      return;
    }
    if (this.#pending) {
      this.#syncRequested = true;
      return;
    }
    if (this.#video && !this.#video.paused) return;
    if (!this.#video) {
      const template = this.querySelector('template');
      if (!template) return;
      this.append(template.content.cloneNode(true));
      this.#video = this.querySelector('video');
      if (!this.#video) return;
      this.#video.muted = true;
    }
    this.#pending = true;
    this.#syncRequested = false;
    try {
      await this.#video.play();
      if (!this.#shouldPlay()) this.#stop();
      else this.setAttribute('playing', '');
    } catch (error) {
      if (error.name !== 'AbortError') this.#failed = true;
      this.#stop();
    } finally {
      this.#pending = false;
      if (this.#syncRequested && this.#shouldPlay()) this.#syncPlayback();
    }
  }
}

if (!customElements.get('product-card-video')) {
  customElements.define('product-card-video', ProductCardVideo);
}
